import {
  Accordion,
  AccordionDetails,
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";

import { Loader } from "@/ui-components/Loader";
import { AccordionSummary } from "@/ui-components/layouts/components/components/Arrordion";
import { MENU_WIDTH } from "@/ui-components/layouts/constants";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorAlert } from "@/ErrorHandling/ErrorAlert";
import { useParsedError } from "@/ErrorHandling/hooks";
import {
  useApiV1AssessmentsAssessmentsRetrieveQuery,
  useApiV1AssessmentsAssessmentsReviewAssignmentsStepsListQuery,
} from "@/services/api";
import { ConditionalLink } from "@/ui-components/ConditionalLink";
import { useParams } from "react-router";

const useGetData = (assessmentId: string | undefined) => {
  const {
    data: steps,
    isLoading: stepsIsLoading,
    error: stepsError,
  } = useApiV1AssessmentsAssessmentsReviewAssignmentsStepsListQuery(
    {
      assessmentId: assessmentId!,
    },
    {
      skip: !assessmentId,
    },
  );

  const {
    data: assessment,
    isLoading: assessmentIsLoading,
    error: assessmentError,
  } = useApiV1AssessmentsAssessmentsRetrieveQuery(
    { assessmentId: assessmentId! },
    {
      skip: !assessmentId,
    },
  );
  const error = useParsedError(stepsError || assessmentError);

  return {
    steps,
    assessment,
    isLoading: stepsIsLoading || assessmentIsLoading,
    error,
  };
};

export const AssignReviewersMenu = () => {
  const { t } = useTranslation();
  const { assessmentId, stepId } = useParams<{ assessmentId: string; stepId: string }>();
  const { steps, isLoading, assessment, error } = useGetData(assessmentId);

  if (isLoading) {
    return <Loader sx={{ width: MENU_WIDTH }} />;
  }

  if (!steps || !assessment) {
    return <ErrorAlert error={error!} />;
  }

  return (
    <Box
      sx={{
        height: "100%",
        overflow: "auto",
        width: MENU_WIDTH,
      }}
    >
      <Accordion expanded>
        <AccordionSummary
          sx={{
            pr: 0,
            "& .MuiAccordionSummary-content": {
              justifyContent: "space-between",
              alignItems: "center",
            },
            "& .MuiAccordionSummary-expandIconWrapper": {
              display: "none",
            },
          }}
        >
          <Typography>{t("assign_reviewers.Steps")}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          <List
            component="div"
            sx={{
              px: 0,
              py: 0,
            }}
          >
            {steps?.map(step => (
              <ListItem
                component="div"
                sx={{
                  px: 0,
                  py: 0,
                }}
                key={step.id}
                data-testid={`step-${step.id}`}
              >
                <ListItemButton
                  component={Link}
                  to={`/assessments/${assessmentId}/reviewers/${step.id}`}
                  sx={[
                    {
                      px: 2,
                      pl: "40px",
                      paddingRight: "32px",
                    },
                    step.id.toString() === stepId
                      ? {
                          background: "#E0F2EF",
                        }
                      : {
                          background: "unset",
                        },
                  ]}
                >
                  <ListItemText
                    slotProps={{
                      primary: {
                        sx: {
                          display: "flex",
                          justifyContent: "space-between",
                        },
                      },
                    }}
                  >
                    <Typography
                      data-testid={`step-${step.id}-name`}
                      sx={{
                        color: "var(--grey-scale-900)",
                        fontSize: "14px",
                        fontWeight: "700",
                        lineHeight: "20px",
                      }}
                    >
                      {step.name}
                    </Typography>
                    <Typography
                      data-testid={`step-${step.id}-due-date`}
                      sx={{
                        color: "var(--grey-scale-900)",
                        fontSize: "14px",
                        lineHeight: "20px",
                      }}
                    >
                      {step.due_date}
                    </Typography>
                  </ListItemText>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>
      <Box
        sx={{
          textAlign: "center",
        }}
      >
        <ConditionalLink disabled={assessment.completed} to={`/assessments/${assessmentId}/edit`}>
          <Button
            startIcon={<EditOutlinedIcon />}
            variant="text"
            disabled={assessment.completed}
            data-testid="edit-steps-button"
          >
            {t("assign_reviewers.Edit_steps")}
          </Button>
        </ConditionalLink>
      </Box>
    </Box>
  );
};
